import { useRef, useEffect, useState, useCallback } from 'react';
import { useAnimation } from '@/context/AnimationContext';
import { useI18n } from '@/context/I18nContext';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Volume2, VolumeX, Music } from 'lucide-react';

// Simple waltz melody using Web Audio API
const WALTZ_NOTES = [
  // [frequency, duration_beats, type] - 3/4 time
  // Bar 1
  [329.63, 1, 'melody'], [392.00, 0.5, 'melody'], [440.00, 0.5, 'melody'], [392.00, 1, 'melody'],
  // Bar 2
  [440.00, 1, 'melody'], [523.25, 0.5, 'melody'], [493.88, 0.5, 'melody'], [440.00, 1, 'melody'],
  // Bar 3
  [392.00, 1, 'melody'], [329.63, 0.5, 'melody'], [349.23, 0.5, 'melody'], [392.00, 1, 'melody'],
  // Bar 4
  [329.63, 1.5, 'melody'], [293.66, 0.5, 'melody'], [329.63, 1, 'melody'],
  // Bar 5
  [349.23, 1, 'melody'], [392.00, 0.5, 'melody'], [440.00, 0.5, 'melody'], [523.25, 1, 'melody'],
  // Bar 6
  [493.88, 1, 'melody'], [440.00, 0.5, 'melody'], [392.00, 0.5, 'melody'], [349.23, 1, 'melody'],
  // Bar 7
  [329.63, 1, 'melody'], [293.66, 0.5, 'melody'], [329.63, 0.5, 'melody'], [349.23, 1, 'melody'],
  // Bar 8
  [329.63, 2, 'melody'], [0, 1, 'rest'],
];

const BASS_NOTES = [
  // Per bar - [frequency, beats]
  [130.81, 3], // C3
  [164.81, 3], // E3
  [174.61, 3], // F3
  [130.81, 3], // C3
  [174.61, 3], // F3
  [164.81, 3], // E3
  [130.81, 3], // C3
  [130.81, 3], // C3
];

export function AudioPlayer() {
  const { state } = useAnimation();
  const { t } = useI18n();
  const [muted, setMuted] = useState(true); // Start muted - no AudioContext until user clicks
  const [volume, setVolume] = useState(30);
  const audioCtxRef = useRef(null);
  const gainRef = useRef(null);
  const scheduledRef = useRef([]);
  const isPlayingRef = useRef(false);
  const startTimeRef = useRef(0);
  const userInteractedRef = useRef(false); // Only create AudioContext after user gesture

  const BPM = 100;
  const beatDuration = 60 / BPM;

  const cleanup = useCallback(() => {
    scheduledRef.current.forEach(node => {
      try { node.stop(); node.disconnect(); } catch (e) { /* ignore */ }
    });
    scheduledRef.current = [];
  }, []);

  const playNote = useCallback((freq, startTime, duration, type = 'melody') => {
    if (!audioCtxRef.current || !gainRef.current || freq === 0) return;
    const ctx = audioCtxRef.current;

    const osc = ctx.createOscillator();
    const noteGain = ctx.createGain();

    osc.type = type === 'melody' ? 'triangle' : 'sine';
    osc.frequency.setValueAtTime(freq, startTime);

    const attackTime = 0.02;
    const releaseTime = Math.min(0.15, duration * 0.3);
    const amp = type === 'melody' ? 0.12 : 0.06;

    noteGain.gain.setValueAtTime(0, startTime);
    noteGain.gain.linearRampToValueAtTime(amp, startTime + attackTime);
    noteGain.gain.setValueAtTime(amp, startTime + duration - releaseTime);
    noteGain.gain.linearRampToValueAtTime(0, startTime + duration);

    osc.connect(noteGain);
    noteGain.connect(gainRef.current);

    osc.start(startTime);
    osc.stop(startTime + duration + 0.01);

    scheduledRef.current.push(osc);
  }, []);

  const scheduleMusic = useCallback((fromTime) => {
    if (!audioCtxRef.current) return;
    const ctx = audioCtxRef.current;
    const now = ctx.currentTime;

    cleanup();

    // Calculate how many full loops we need for 2 minutes
    const totalMelodyBeats = WALTZ_NOTES.reduce((sum, n) => sum + n[1], 0);
    const loopDuration = totalMelodyBeats * beatDuration;
    const totalDuration = 130; // slightly over 2 min

    for (let loopStart = 0; loopStart < totalDuration; loopStart += loopDuration) {
      // Melody
      let melodyTime = loopStart;
      for (const [freq, beats, type] of WALTZ_NOTES) {
        const dur = beats * beatDuration;
        const schedTime = now + melodyTime - fromTime;
        if (schedTime >= now - 0.1) {
          playNote(freq, Math.max(now, schedTime), dur, type);
        }
        melodyTime += dur;
      }

      // Bass
      let bassTime = loopStart;
      for (const [freq, beats] of BASS_NOTES) {
        const dur = beats * beatDuration;
        const schedTime = now + bassTime - fromTime;
        if (schedTime >= now - 0.1) {
          playNote(freq, Math.max(now, schedTime), dur * 0.8, 'bass');
        }
        bassTime += dur;
      }
    }
  }, [beatDuration, cleanup, playNote]);

  // Sync with playback - only when user has interacted
  useEffect(() => {
    if (state.isPlaying && !muted && userInteractedRef.current) {
      try {
        if (!audioCtxRef.current) {
          audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
          gainRef.current = audioCtxRef.current.createGain();
          gainRef.current.connect(audioCtxRef.current.destination);
        }

        if (audioCtxRef.current.state === 'suspended') {
          audioCtxRef.current.resume().catch(() => {});
        }

        gainRef.current.gain.setValueAtTime(volume / 100, audioCtxRef.current.currentTime);
        const animTime = state.currentFrame / state.project.fps;
        scheduleMusic(animTime);
        isPlayingRef.current = true;
        startTimeRef.current = audioCtxRef.current.currentTime - animTime;
      } catch (e) {
        // AudioContext not available
      }
    } else {
      cleanup();
      isPlayingRef.current = false;
      if (audioCtxRef.current && audioCtxRef.current.state === 'running') {
        audioCtxRef.current.suspend().catch(() => {});
      }
    }

    return () => {
      if (!state.isPlaying) cleanup();
    };
  }, [state.isPlaying, muted]);

  // Volume change - only when context exists
  useEffect(() => {
    if (gainRef.current && audioCtxRef.current && audioCtxRef.current.state === 'running') {
      try {
        gainRef.current.gain.setValueAtTime(muted ? 0 : volume / 100, audioCtxRef.current.currentTime);
      } catch (e) { /* ignore */ }
    }
  }, [volume, muted]);

  return (
    <div className="flex items-center gap-1.5" data-testid="audio-player">
      <Button
        variant="ghost"
        size="sm"
        className={`h-6 w-6 p-0 rounded-full ${muted ? 'text-muted-foreground/30' : 'text-rose-300/60'}`}
        onClick={() => { userInteractedRef.current = true; setMuted(!muted); }}
        data-testid="audio-mute-toggle"
      >
        {muted ? <VolumeX className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
      </Button>
      <Slider
        value={[muted ? 0 : volume]}
        onValueChange={([v]) => { userInteractedRef.current = true; setVolume(v); if (v > 0 && muted) setMuted(false); }}
        min={0}
        max={100}
        step={5}
        className="w-16"
        data-testid="audio-volume-slider"
      />
      <Music className="w-2.5 h-2.5 text-muted-foreground/30" />
    </div>
  );
}
